import { ChangeDetectionStrategy, Component, DestroyRef, input } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { SingleSelectComponent } from '@examdojo/core/select';
import { ContentItemEditingOptions } from '@examdojo/models/question-editor';
import { NumberInputComponent } from '@examdojo/ui/input';
import { MultiLineTextComponent } from '@examdojo/ui/multi-line-text';
import { debounceTime, distinctUntilChanged, map, merge, scan, startWith, switchMap, tap } from 'rxjs';
import { TableContentItemFormGroup } from '../question-content-item.model';

@Component({
  selector: 'dojo-question-table-editor',
  templateUrl: './question-table-editor.component.html',
  imports: [
    ReactiveFormsModule,
    MatTooltipModule,
    MatSlideToggle,
    MultiLineTextComponent,
    NumberInputComponent,
    SingleSelectComponent,
  ],
  styleUrls: ['./question-table-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col h-full',
  },
  styles: [
    `
      .badge-above {
        @apply rounded px-2 py-1 text-xs font-medium tracking-wide;
      }
    `,
  ],
})
export class QuestionTableEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    // Sync FROM main form TO child controls (one direction only)
    this.formCtrlValue$
      .pipe(
        tap((formCtrlValue) => {
          // Update child controls from main form - no circular dependency
          this.contentFormCtrl.setValue(formCtrlValue.content ?? '', { emitEvent: false });
          this.titleFormCtrl.setValue(formCtrlValue.title ?? '', { emitEvent: false });

          const containerLayout = formCtrlValue.container_width || '100%';
          this.containerLayoutFormCtrl.setValue(containerLayout, { emitEvent: false });

          const style = (formCtrlValue.style || {}) as Record<string, string>;

          this.paddingTopFormCtrl.setValue(this.extractPaddingValue(style['padding-top'] ?? '0'), { emitEvent: false });
          this.paddingRightFormCtrl.setValue(this.extractPaddingValue(style['padding-right'] ?? '0'), {
            emitEvent: false,
          });
          this.paddingBottomFormCtrl.setValue(this.extractPaddingValue(style['padding-bottom'] ?? '0'), {
            emitEvent: false,
          });
          this.paddingLeftFormCtrl.setValue(this.extractPaddingValue(style['padding-left'] ?? '0'), {
            emitEvent: false,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Sync FROM child controls TO main form (one direction only)
    this.setupMainFormUpdates();
  }

  readonly formCtrl = input.required<TableContentItemFormGroup>();
  readonly editingOptions = input<ContentItemEditingOptions>({
    allowTextEditing: true,
    allowImageEditing: [],
    allowFillInTheBlanks: true,
  });

  // Form controls for table properties
  readonly titleFormCtrl = new FormControl<string>('', {
    nonNullable: true,
  });

  readonly containerLayoutFormCtrl = new FormControl<string>('100%', {
    nonNullable: true,
  });

  readonly containerLayoutOptions = [
    { value: '100%', label: '100%' },
    { value: '75%', label: '75%' },
    { value: '50%', label: '50%' },
    { value: '25%', label: '25%' },
  ];

  readonly contentFormCtrl = new FormControl<string>('', {
    nonNullable: true,
  });

  // Padding controls
  readonly paddingTopFormCtrl = new FormControl<number>(0, {
    nonNullable: true,
  });

  readonly paddingRightFormCtrl = new FormControl<number>(0, {
    nonNullable: true,
  });

  readonly paddingBottomFormCtrl = new FormControl<number>(0, {
    nonNullable: true,
  });

  readonly paddingLeftFormCtrl = new FormControl<number>(0, {
    nonNullable: true,
  });

  // Main form value stream - this is our single source of truth
  readonly formCtrlValue$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) => formCtrl.valueChanges.pipe(startWith(formCtrl.value))),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
  );

  // Remove duplicate form control definitions
  readonly state = connectState({
    formCtrlValue: this.formCtrlValue$,
  });

  private setupMainFormUpdates() {
    // Content changes update main form directly
    this.contentFormCtrl.valueChanges
      .pipe(
        debounceTime(100), // Debounce to prevent excessive updates
        tap((contentValue) => {
          this.formCtrl().controls.content.setValue(contentValue || '');
          this.formCtrl().controls.content.markAsDirty();
          this.formCtrl().controls.content.markAsTouched();
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Title changes update main form directly
    this.titleFormCtrl.valueChanges
      .pipe(
        debounceTime(100),
        tap((titleValue) => {
          this.formCtrl().controls.title.setValue(titleValue || undefined);
          this.formCtrl().controls.title.markAsDirty();
          this.formCtrl().controls.title.markAsTouched();
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Container layout changes update main form directly
    this.containerLayoutFormCtrl.valueChanges
      .pipe(
        debounceTime(100),
        tap((containerLayoutValue) => {
          this.formCtrl().controls.container_width.setValue(containerLayoutValue);
          this.formCtrl().controls.container_width.markAsDirty();
          this.formCtrl().controls.container_width.markAsTouched();
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Padding changes update main form style property
    merge(
      this.paddingTopFormCtrl.valueChanges.pipe(map((value) => ({ 'padding-top': `${value ?? 0}px` }))),
      this.paddingRightFormCtrl.valueChanges.pipe(map((value) => ({ 'padding-right': `${value ?? 0}px` }))),
      this.paddingBottomFormCtrl.valueChanges.pipe(map((value) => ({ 'padding-bottom': `${value ?? 0}px` }))),
      this.paddingLeftFormCtrl.valueChanges.pipe(map((value) => ({ 'padding-left': `${value ?? 0}px` }))),
    )
      .pipe(
        scan((acc, curr) => ({ ...acc, ...curr }), {} as Record<string, string>),
        debounceTime(100),
        tap((style) => this.updateStyleProperties(style)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private updateStyleProperties(style: Record<string, string>) {
    const currentStyle = (this.formCtrl().controls.style.value as CSSStyleDeclaration) || {};
    const updatedStyle = { ...currentStyle, ...style };

    this.formCtrl().controls.style.setValue(updatedStyle);
    this.formCtrl().controls.style.markAsDirty();
    this.formCtrl().controls.style.markAsTouched();
  }

  private extractPaddingValue(value: string): number {
    if (!value || value === '0') {
      return 0;
    }
    // Extract number from strings like "10px", "5rem", etc.
    const match = value.match(/^(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }
}
